import React, { useState } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Search, Calendar, User, Tag, ExternalLink } from 'lucide-react';
export function BlogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [emailSubscribed, setEmailSubscribed] = useState(false);
  const handleFilterClick = (filter: string) => {
    setActiveFilter(filter);
  };
  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailSubscribed(true);
    setTimeout(() => {
      setEmailSubscribed(false);
    }, 5000);
  };
  // Sample blog posts data
  const blogPosts = [{
    id: 1,
    title: 'From Surplus to Service: The Journey of Medical Supplies',
    excerpt: 'Follow the path of medical supplies from U.S. hospitals to Egyptian clinics, and the impact they make along the way.',
    image: 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Impact',
    author: 'Natasha Khalil',
    date: 'June 15, 2023',
    tags: ['Impact', 'Supplies', 'Process']
  }, {
    id: 2,
    title: 'Meet the Volunteers: Hands and Hearts Behind Farasha',
    excerpt: 'Our dedicated volunteers share their stories and why they believe in our mission to bridge healthcare gaps.',
    image: 'https://images.unsplash.com/photo-1593113630400-ea4288922497?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Field Notes',
    author: 'Sarah Johnson',
    date: 'May 22, 2023',
    tags: ['Volunteers', 'Stories', 'Community']
  }, {
    id: 3,
    title: 'Hospital Partnership Spotlight: Memorial Health',
    excerpt: "How one hospital's commitment to reducing waste became a lifeline for clinics serving Coptic communities in Egypt.",
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Partners',
    author: 'Michael Chen',
    date: 'April 10, 2023',
    tags: ['Partners', 'Hospitals', 'Sustainability']
  }, {
    id: 4,
    title: 'How to Host a Medical Supply Drive at Your Workplace',
    excerpt: 'A step-by-step guide to organizing an effective medical supply drive that engages colleagues and makes an impact.',
    image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'How-To',
    author: 'Natasha Khalil',
    date: 'March 5, 2023',
    tags: ['Guide', 'Workplace', 'Donations']
  }, {
    id: 5,
    title: 'The Hidden Healthcare Heroes of Upper Egypt',
    excerpt: 'Exploring the critical role of monastery clinics in providing healthcare to underserved communities across Egypt.',
    image: 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Impact',
    author: 'David Wilson',
    date: 'February 18, 2023',
    tags: ['Healthcare', 'Egypt', 'Communities']
  }, {
    id: 6,
    title: 'Medical Waste Reduction: A Global Imperative',
    excerpt: 'How initiatives like Farasha are part of a global movement to reduce medical waste while addressing healthcare inequities.',
    image: 'https://images.unsplash.com/photo-1530533718754-001d2668365a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    category: 'Field Notes',
    author: 'Emma Rodriguez',
    date: 'January 24, 2023',
    tags: ['Environment', 'Sustainability', 'Global Health']
  }];
  // Filter posts based on active filter and search term
  const filteredPosts = blogPosts.filter(post => {
    const matchesFilter = activeFilter === 'all' || post.category.toLowerCase() === activeFilter;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) || post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-303">
        <div className="text-center mb-12" data-id="element-304">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-305">
            Stories & Updates
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-306">
            Explore stories of impact, partner spotlights, field notes, and
            practical guides from our work bridging healthcare gaps.
          </p>
        </div>
        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto" data-id="element-307">
          <div className="bg-white p-4 rounded-xl shadow-md" data-id="element-308">
            <div className="relative mb-4" data-id="element-309">
              <input type="text" placeholder="Search articles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-310" />
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-charcoal/60" data-id="element-311" />
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-312">
              <button onClick={() => handleFilterClick('all')} className={`px-3 py-1 rounded-md font-inter text-sm ${activeFilter === 'all' ? 'bg-primary-royal-blue text-white' : 'bg-neutral-offwhite text-neutral-charcoal hover:bg-neutral-charcoal/10'}`} data-id="element-313">
                All
              </button>
              <button onClick={() => handleFilterClick('impact')} className={`px-3 py-1 rounded-md font-inter text-sm ${activeFilter === 'impact' ? 'bg-primary-royal-blue text-white' : 'bg-neutral-offwhite text-neutral-charcoal hover:bg-neutral-charcoal/10'}`} data-id="element-314">
                Impact
              </button>
              <button onClick={() => handleFilterClick('partners')} className={`px-3 py-1 rounded-md font-inter text-sm ${activeFilter === 'partners' ? 'bg-primary-royal-blue text-white' : 'bg-neutral-offwhite text-neutral-charcoal hover:bg-neutral-charcoal/10'}`} data-id="element-315">
                Partners
              </button>
              <button onClick={() => handleFilterClick('field notes')} className={`px-3 py-1 rounded-md font-inter text-sm ${activeFilter === 'field notes' ? 'bg-primary-royal-blue text-white' : 'bg-neutral-offwhite text-neutral-charcoal hover:bg-neutral-charcoal/10'}`} data-id="element-316">
                Field Notes
              </button>
              <button onClick={() => handleFilterClick('how-to')} className={`px-3 py-1 rounded-md font-inter text-sm ${activeFilter === 'how-to' ? 'bg-primary-royal-blue text-white' : 'bg-neutral-offwhite text-neutral-charcoal hover:bg-neutral-charcoal/10'}`} data-id="element-317">
                How-To
              </button>
            </div>
          </div>
        </div>
      </Section>
      {/* Blog Posts Grid */}
      <Section background="white" data-id="element-318">
        <div className="grid md:grid-cols-3 gap-8" data-id="element-319">
          {/* Main Content */}
          <div className="md:col-span-2" data-id="element-320">
            {filteredPosts.length === 0 ? <div className="text-center py-12" data-id="element-321">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-322">
                  No articles found
                </h3>
                <p className="font-inter text-neutral-charcoal" data-id="element-323">
                  Try adjusting your search or filter criteria
                </p>
              </div> : <div className="grid md:grid-cols-2 gap-6" data-id="element-324">
                {filteredPosts.map(post => <article key={post.id} className="bg-white border border-neutral-charcoal/10 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-325">
                    <div className="h-48 overflow-hidden" data-id="element-326">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="element-327" />
                    </div>
                    <div className="p-6" data-id="element-328">
                      <div className="flex items-center gap-2 mb-2" data-id="element-329">
                        <span className="inline-block px-2 py-1 bg-neutral-offwhite rounded-md text-xs font-inter font-medium text-primary-royal-blue" data-id="element-330">
                          {post.category}
                        </span>
                      </div>
                      <h2 className="text-xl font-playfair font-bold text-primary-deep-blue mb-2 hover:text-primary-royal-blue transition-colors" data-id="element-331">
                        <a href={`/blog/${post.id}`} data-id="element-332">{post.title}</a>
                      </h2>
                      <p className="font-inter text-neutral-charcoal mb-4" data-id="element-333">
                        {post.excerpt}
                      </p>
                      <div className="flex justify-between items-center text-sm font-inter text-neutral-charcoal/70" data-id="element-334">
                        <div className="flex items-center" data-id="element-335">
                          <User size={14} className="mr-1" data-id="element-336" />
                          {post.author}
                        </div>
                        <div className="flex items-center" data-id="element-337">
                          <Calendar size={14} className="mr-1" data-id="element-338" />
                          {post.date}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2" data-id="element-339">
                        {post.tags.map((tag, index) => <div key={index} className="flex items-center text-xs font-inter text-neutral-charcoal/60" data-id="element-340">
                            <Tag size={12} className="mr-1" data-id="element-341" />
                            {tag}
                          </div>)}
                      </div>
                    </div>
                  </article>)}
              </div>}
          </div>
          {/* Sidebar */}
          <div className="space-y-8" data-id="element-342">
            {/* Newsletter Signup */}
            <div className="bg-neutral-sand p-6 rounded-xl shadow-md" data-id="element-343">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-344">
                Get Monthly Impact Updates
              </h3>
              <p className="font-inter text-neutral-charcoal mb-4" data-id="element-345">
                Join our newsletter to receive the latest stories, impact
                reports, and ways to get involved.
              </p>
              {emailSubscribed ? <div className="bg-primary-royal-blue/10 text-primary-royal-blue p-3 rounded-md text-center font-inter" data-id="element-346">
                  Thank you for subscribing!
                </div> : <form onSubmit={handleSubscribe} data-id="element-347">
                  <div className="mb-3" data-id="element-348">
                    <input type="email" placeholder="Your email address" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-349" />
                  </div>
                  <Button type="submit" className="w-full" data-id="element-350">
                    Subscribe
                  </Button>
                </form>}
            </div>
            {/* Support Today */}
            <div className="bg-white border border-neutral-charcoal/10 p-6 rounded-xl shadow-md" data-id="element-351">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-352">
                Support Today
              </h3>
              <p className="font-inter text-neutral-charcoal mb-4" data-id="element-353">
                Your donation helps us continue our mission of bridging
                healthcare gaps for underserved communities.
              </p>
              <div className="space-y-3" data-id="element-354">
                <Button href="/donate" className="w-full" data-id="element-355">
                  Donate
                </Button>
                <Button variant="secondary" className="w-full flex items-center justify-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-356">
                  Shop Wish List <ExternalLink size={16} data-id="element-357" />
                </Button>
              </div>
            </div>
            {/* Popular Tags */}
            <div className="bg-white border border-neutral-charcoal/10 p-6 rounded-xl shadow-md" data-id="element-358">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-359">
                Popular Tags
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-360">
                {['Impact', 'Volunteers', 'Healthcare', 'Egypt', 'Supplies', 'Sustainability', 'Partners', 'Guide', 'Stories', 'Community'].map((tag, index) => <button key={index} onClick={() => setSearchTerm(tag)} className="px-3 py-1 bg-neutral-offwhite rounded-md text-sm font-inter text-neutral-charcoal hover:bg-neutral-charcoal/10" data-id="element-361">
                    {tag}
                  </button>)}
              </div>
            </div>
            {/* Recent Posts */}
            <div className="bg-white border border-neutral-charcoal/10 p-6 rounded-xl shadow-md" data-id="element-362">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-363">
                Recent Posts
              </h3>
              <div className="space-y-4" data-id="element-364">
                {blogPosts.slice(0, 3).map(post => <div key={post.id} className="flex gap-3" data-id="element-365">
                    <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden" data-id="element-366">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-367" />
                    </div>
                    <div data-id="element-368">
                      <h4 className="font-inter font-medium text-neutral-charcoal hover:text-primary-royal-blue transition-colors" data-id="element-369">
                        <a href={`/blog/${post.id}`} data-id="element-370">{post.title}</a>
                      </h4>
                      <div className="text-xs font-inter text-neutral-charcoal/60 mt-1" data-id="element-371">
                        {post.date}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-372">
        <div className="text-center" data-id="element-373">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-374">
            Make a difference today
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-375">
            Help us continue sharing stories of impact and bridging healthcare
            gaps for underserved communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-376">
            <Button href="/donate" size="lg" data-id="element-377">
              Donate Now
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-378">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}